import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, Save, Copy } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

const reId = (o) => {
  if (Array.isArray(o)) { o.forEach(reId); return o; }
  if (o && typeof o === "object") {
    if ("id" in o) o.id = uid();
    Object.values(o).forEach(reId);
  }
  return o;
};
const dup = (o) => reId(JSON.parse(JSON.stringify(o)));

export default function ProgramBuilder() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [program, setProgram] = useState({ name: "", description: "", phases: [] });
  const [sel, setSel] = useState({ phase: 0, month: 0, week: 0, routine: 0 });
  const [exercises, setExercises] = useState([]);

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  useEffect(() => {
    api.get("/exercises").then((r) => setExercises(r.data));
    if (id) {
      api.get(`/programs/${id}`).then((r) => setProgram(r.data));
    } else {
      try {
        const draft = sessionStorage.getItem("ai_draft_program");
        if (draft) {
          sessionStorage.removeItem("ai_draft_program");
          setProgram(JSON.parse(draft));
          toast.success(t("pb.aiDraftLoaded"));
        }
      } catch { /* ignore */ }
    }
  }, [id]);

  const steps = [t("pb.step1"), t("pb.step2"), t("pb.step3"), t("pb.step4"), t("pb.step5")];

  const phase = program.phases[sel.phase];
  const month = phase?.months?.[sel.month];
  const week = month?.weeks?.[sel.week];
  const routine = week?.routines?.[sel.routine];

  const updatePhase = (fn) => {
    const phases = [...program.phases];
    phases[sel.phase] = fn({ ...phases[sel.phase] });
    setProgram({ ...program, phases });
  };
  const updateMonth = (fn) => updatePhase((p) => {
    const months = [...(p.months || [])];
    months[sel.month] = fn({ ...months[sel.month] });
    return { ...p, months };
  });
  const updateWeek = (fn) => updateMonth((m) => {
    const weeks = [...(m.weeks || [])];
    weeks[sel.week] = fn({ ...weeks[sel.week] });
    return { ...m, weeks };
  });
  const updateRoutine = (fn) => updateWeek((w) => {
    const routines = [...(w.routines || [])];
    routines[sel.routine] = fn({ ...routines[sel.routine] });
    return { ...w, routines };
  });

  const Selector = ({ label, items, value, onChange, testid }) => (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(Number(e.target.value))} className="kc-input mt-1">
        {(items || []).map((it, i) => <option key={it.id} value={i}>{it.name}</option>)}
        {(!items || items.length === 0) && <option value={0}>—</option>}
      </select>
    </div>
  );

  const save = async () => {
    try {
      if (id) await api.put(`/programs/${id}`, program);
      else await api.post("/programs", program);
      toast.success(t("pb.saved"));
      navigate("/coach/programs");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Layout nav={nav}>
      <div data-testid="program-builder">
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-6">
          {id ? t("common.edit") : t("pr.new")}
        </h1>

        <div className="flex gap-1 mb-8 overflow-x-auto" data-testid="wizard-steps">
          {steps.map((s, i) => (
            <button key={s} data-testid={`wizard-step-${i}`} onClick={() => setStep(i)}
              className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${
                i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-secondary text-foreground" : "bg-secondary/40 text-muted-foreground"
              }`}>
              {i + 1}. {s}
            </button>
          ))}
        </div>

        <div className="kc-card p-6 space-y-4 min-h-[300px]">
          {step === 0 && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("pb.name")}</label>
                  <input data-testid="program-name-input" value={program.name}
                    onChange={(e) => setProgram({ ...program, name: e.target.value })} className="kc-input mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("pb.desc")}</label>
                  <input data-testid="program-desc-input" value={program.description}
                    onChange={(e) => setProgram({ ...program, description: e.target.value })} className="kc-input mt-1" />
                </div>
              </div>
              <div className="space-y-2">
                {program.phases.map((p, i) => (
                  <div key={p.id} className="flex gap-2 items-center bg-secondary/40 rounded-lg p-3" data-testid={`phase-row-${i}`}>
                    <input data-testid={`phase-name-${i}`} value={p.name} placeholder={t("pb.phaseName")}
                      onChange={(e) => { const phases = [...program.phases]; phases[i] = { ...phases[i], name: e.target.value }; setProgram({ ...program, phases }); }}
                      className="kc-input flex-1" />
                    <input data-testid={`phase-goal-${i}`} value={p.goal || ""} placeholder={t("pb.phaseGoal")}
                      onChange={(e) => { const phases = [...program.phases]; phases[i] = { ...phases[i], goal: e.target.value }; setProgram({ ...program, phases }); }}
                      className="kc-input flex-1" />
                    <button data-testid={`phase-copy-${i}`} title={t("pb.duplicate")}
                      onClick={() => { const phases = [...program.phases]; const c = dup(phases[i]); c.name = `${c.name} (copia)`; phases.splice(i + 1, 0, c); setProgram({ ...program, phases }); }}
                      className="kc-btn-ghost !p-2 text-muted-foreground hover:text-primary"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`phase-delete-${i}`} onClick={() => setProgram({ ...program, phases: program.phases.filter((_, j) => j !== i) })}
                      className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid="add-phase-button" className="kc-btn-secondary flex items-center gap-2"
                  onClick={() => setProgram({ ...program, phases: [...program.phases, { id: uid(), name: `${t("coach.phase")} ${program.phases.length + 1}`, goal: "", months: [] }] })}>
                  <Plus className="w-4 h-4" /> {t("pb.addPhase")}
                </button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <Selector label={t("pb.selectPhase")} items={program.phases} value={sel.phase}
                onChange={(v) => setSel({ ...sel, phase: v, month: 0, week: 0, routine: 0 })} testid="select-phase" />
              {phase && (
                <div className="space-y-2">
                  {(phase.months || []).map((m, i) => (
                    <div key={m.id} className="flex gap-2 items-center bg-secondary/40 rounded-lg p-3" data-testid={`month-row-${i}`}>
                      <input data-testid={`month-name-${i}`} value={m.name} placeholder={t("pb.monthName")}
                        onChange={(e) => updatePhase((p) => { const months = [...p.months]; months[i] = { ...months[i], name: e.target.value }; return { ...p, months }; })}
                        className="kc-input flex-1" />
                      <button data-testid={`month-copy-${i}`} title={t("pb.duplicate")}
                        onClick={() => updatePhase((p) => { const months = [...p.months]; const c = dup(months[i]); c.name = `${c.name} (copia)`; months.splice(i + 1, 0, c); return { ...p, months }; })}
                        className="kc-btn-ghost !p-2 text-muted-foreground hover:text-primary"><Copy className="w-4 h-4" /></button>
                      <button data-testid={`month-delete-${i}`} onClick={() => updatePhase((p) => ({ ...p, months: p.months.filter((_, j) => j !== i) }))}
                        className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button data-testid="add-month-button" className="kc-btn-secondary flex items-center gap-2"
                    onClick={() => updatePhase((p) => ({ ...p, months: [...(p.months || []), { id: uid(), name: `${t("pb.step2").slice(0, -1)} ${(p.months || []).length + 1}`, weeks: [] }] }))}>
                    <Plus className="w-4 h-4" /> {t("pb.addMonth")}
                  </button>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Selector label={t("pb.selectPhase")} items={program.phases} value={sel.phase}
                  onChange={(v) => setSel({ ...sel, phase: v, month: 0, week: 0, routine: 0 })} testid="select-phase" />
                <Selector label={t("pb.selectMonth")} items={phase?.months} value={sel.month}
                  onChange={(v) => setSel({ ...sel, month: v, week: 0, routine: 0 })} testid="select-month" />
              </div>
              {month && (
                <div className="space-y-2">
                  {(month.weeks || []).map((w, i) => (
                    <div key={w.id} className="flex gap-2 items-center bg-secondary/40 rounded-lg p-3" data-testid={`week-row-${i}`}>
                      <input data-testid={`week-name-${i}`} value={w.name} placeholder={t("pb.weekName")}
                        onChange={(e) => updateMonth((m) => { const weeks = [...m.weeks]; weeks[i] = { ...weeks[i], name: e.target.value }; return { ...m, weeks }; })}
                        className="kc-input flex-1" />
                      <input data-testid={`week-focus-${i}`} value={w.focus || ""} placeholder={t("pb.weekFocus")}
                        onChange={(e) => updateMonth((m) => { const weeks = [...m.weeks]; weeks[i] = { ...weeks[i], focus: e.target.value }; return { ...m, weeks }; })}
                        className="kc-input flex-1" />
                      <button data-testid={`week-copy-${i}`} title={t("pb.duplicate")}
                        onClick={() => updateMonth((m) => { const weeks = [...m.weeks]; const c = dup(weeks[i]); c.name = `${c.name} (copia)`; weeks.splice(i + 1, 0, c); return { ...m, weeks }; })}
                        className="kc-btn-ghost !p-2 text-muted-foreground hover:text-primary"><Copy className="w-4 h-4" /></button>
                      <button data-testid={`week-delete-${i}`} onClick={() => updateMonth((m) => ({ ...m, weeks: m.weeks.filter((_, j) => j !== i) }))}
                        className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button data-testid="add-week-button" className="kc-btn-secondary flex items-center gap-2"
                    onClick={() => updateMonth((m) => ({ ...m, weeks: [...(m.weeks || []), { id: uid(), name: `${t("client.week")} ${(m.weeks || []).length + 1}`, focus: "", routines: [] }] }))}>
                    <Plus className="w-4 h-4" /> {t("pb.addWeek")}
                  </button>
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid sm:grid-cols-3 gap-4">
                <Selector label={t("pb.selectPhase")} items={program.phases} value={sel.phase}
                  onChange={(v) => setSel({ ...sel, phase: v, month: 0, week: 0, routine: 0 })} testid="select-phase" />
                <Selector label={t("pb.selectMonth")} items={phase?.months} value={sel.month}
                  onChange={(v) => setSel({ ...sel, month: v, week: 0, routine: 0 })} testid="select-month" />
                <Selector label={t("pb.selectWeek")} items={month?.weeks} value={sel.week}
                  onChange={(v) => setSel({ ...sel, week: v, routine: 0 })} testid="select-week" />
              </div>
              {week && (
                <div className="space-y-2">
                  {(week.routines || []).map((r, i) => (
                    <div key={r.id} className="flex gap-2 items-center bg-secondary/40 rounded-lg p-3" data-testid={`routine-row-${i}`}>
                      <input data-testid={`routine-name-${i}`} value={r.name} placeholder={t("pb.routineName")}
                        onChange={(e) => updateWeek((w) => { const routines = [...w.routines]; routines[i] = { ...routines[i], name: e.target.value }; return { ...w, routines }; })}
                        className="kc-input flex-1" />
                      <input data-testid={`routine-day-${i}`} value={r.day || ""} placeholder={t("pb.routineDay")}
                        onChange={(e) => updateWeek((w) => { const routines = [...w.routines]; routines[i] = { ...routines[i], day: e.target.value }; return { ...w, routines }; })}
                        className="kc-input !w-28" />
                      <button data-testid={`routine-copy-${i}`} title={t("pb.duplicate")}
                        onClick={() => updateWeek((w) => { const routines = [...w.routines]; const c = dup(routines[i]); c.name = `${c.name} (copia)`; routines.splice(i + 1, 0, c); return { ...w, routines }; })}
                        className="kc-btn-ghost !p-2 text-muted-foreground hover:text-primary"><Copy className="w-4 h-4" /></button>
                      <button data-testid={`routine-delete-${i}`} onClick={() => updateWeek((w) => ({ ...w, routines: w.routines.filter((_, j) => j !== i) }))}
                        className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button data-testid="add-routine-button" className="kc-btn-secondary flex items-center gap-2"
                    onClick={() => updateWeek((w) => ({ ...w, routines: [...(w.routines || []), { id: uid(), name: `Routine ${(w.routines || []).length + 1}`, day: "", exercises: [] }] }))}>
                    <Plus className="w-4 h-4" /> {t("pb.addRoutine")}
                  </button>
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Selector label={t("pb.selectPhase")} items={program.phases} value={sel.phase}
                  onChange={(v) => setSel({ ...sel, phase: v, month: 0, week: 0, routine: 0 })} testid="select-phase" />
                <Selector label={t("pb.selectMonth")} items={phase?.months} value={sel.month}
                  onChange={(v) => setSel({ ...sel, month: v, week: 0, routine: 0 })} testid="select-month" />
                <Selector label={t("pb.selectWeek")} items={month?.weeks} value={sel.week}
                  onChange={(v) => setSel({ ...sel, week: v, routine: 0 })} testid="select-week" />
                <Selector label={t("pb.selectRoutine")} items={week?.routines} value={sel.routine}
                  onChange={(v) => setSel({ ...sel, routine: v })} testid="select-routine" />
              </div>
              {routine && (
                <div className="space-y-2">
                  {(routine.exercises || []).map((ex, i) => (
                    <div key={i} className="flex flex-wrap gap-2 items-center bg-secondary/40 rounded-lg p-3" data-testid={`builder-exercise-row-${i}`}>
                      <select data-testid={`exercise-select-${i}`} value={ex.exercise_id}
                        onChange={(e) => {
                          const found = exercises.find((x) => x._id === e.target.value);
                          updateRoutine((r) => { const list = [...r.exercises]; list[i] = { ...list[i], exercise_id: e.target.value, name: found?.name || "" }; return { ...r, exercises: list }; });
                        }} className="kc-input flex-1 min-w-[160px]">
                        <option value="">{t("pb.selectExercise")}</option>
                        {exercises.map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
                      </select>
                      {[["sets", t("pb.sets"), 48], ["reps", t("pb.reps"), 64], ["rest_seconds", t("pb.rest"), 64]].map(([k, ph, w]) => (
                        <input key={k} data-testid={`exercise-${k}-${i}`} value={ex[k] ?? ""} placeholder={ph} style={{ width: w + 24 }}
                          onChange={(e) => updateRoutine((r) => { const list = [...r.exercises]; list[i] = { ...list[i], [k]: e.target.value }; return { ...r, exercises: list }; })}
                          className="kc-input !px-2 text-center font-mono" />
                      ))}
                      <input data-testid={`exercise-notes-${i}`} value={ex.notes || ""} placeholder={t("pb.notes")}
                        onChange={(e) => updateRoutine((r) => { const list = [...r.exercises]; list[i] = { ...list[i], notes: e.target.value }; return { ...r, exercises: list }; })}
                        className="kc-input flex-1 min-w-[120px]" />
                      <button data-testid={`exercise-delete-${i}`} onClick={() => updateRoutine((r) => ({ ...r, exercises: r.exercises.filter((_, j) => j !== i) }))}
                        className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button data-testid="add-exercise-button" className="kc-btn-secondary flex items-center gap-2"
                    onClick={() => updateRoutine((r) => ({ ...r, exercises: [...(r.exercises || []), { exercise_id: "", name: "", sets: "3", reps: "10", rest_seconds: "90", notes: "" }] }))}>
                    <Plus className="w-4 h-4" /> {t("pb.addExercise")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <button data-testid="wizard-back-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="kc-btn-secondary flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> {t("pb.back")}</button>
          <div className="flex gap-3">
            <button data-testid="save-program-button" onClick={save} disabled={!program.name.trim()}
              className="kc-btn-primary flex items-center gap-2 !py-2.5"><Save className="w-4 h-4" /> {t("pb.save")}</button>
            {step < 4 && (
              <button data-testid="program-builder-step-next-button" onClick={() => setStep(step + 1)}
                className="kc-btn-secondary flex items-center gap-2">{t("pb.next")} <ChevronRight className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
